import React, { useMemo } from "react";
import {
  Radio,
  Checkbox,
  Divider,
  HTMLSelect,
  FormGroup,
  TextArea
} from "@blueprintjs/core";
import Flexbox from "flexbox-react";
import { isGoalProgressSpecialAccount } from "../../utils/goalProgressUtils.js";
import CategoryRemarkInput from "./CategoryRemarkInput.jsx";

const AssessmentQuestionList = ({
  questions = [],
  remarkQuestions = [],
  answers = [],
  assessmentRatings = {},
  categoryWiseRatings = {},
  selectedTimeframe = {},
  onChange,
  updateAnswer,
  onFileUpload,
  onCheckboxChange,
  onRatingChange,
  weightage,
  isRatingEnabledQuestions,
  accountVisibilityConfig,
  accountId,
  remarkAnswers = {},
  updateRemarkAnswer: updateRemarkAnswerProp,
  stepType,
}) => {
  const useRatingLabels = isGoalProgressSpecialAccount(accountId);
  const ratingLabels = (selectedTimeframe && selectedTimeframe.rating_labels) || [];
  const fallbackRatingLabels = useMemo(
    () => ([
      { label: "Not Met", value: 1 },
      { label: "Partially Met", value: 2 },
      { label: "Met", value: 3 }
    ]),
    []
  );
  console.log("questions Assesme que list", questions);

  const updateRemarkAnswer = (remarkId, data) => {
    updateRemarkAnswerProp(remarkId, data);
  };

  /* ------------------------------------------ */
  /* 1️⃣ Fast Answer Lookup Map                 */
  /* ------------------------------------------ */
  const answerMap = useMemo(() => {
    const map = {};
    answers.forEach(a => {
      const key =
        a.manager_assessment_question_id ||
        a.assessment_question_id ||
        a.id;
      if (key) {
        map[key] = a;
      }
    });
    return map;
  }, [answers]);

  console.log("Answer Map", answerMap);


  const normalizedGroups = useMemo(() => {
    if (!Array.isArray(questions)) return [];

    const groups = [];

    questions.forEach((item, index) => {
      // If item is grouped
      if (Array.isArray(item?.questions)) {
        groups.push({
          category_id:
            item?.category_id === "null" ? null : item?.category_id,
          category_name: item?.category_name?.trim() || "",
          questions: item.questions || [],
          remark_questions: item.remark_questions || [],
          key: item?.category_id || `group-${index}`
        });
      } else {
        // Flat question → move into default group
        let defaultGroup = groups.find(
          g => !g.category_name
        );

        if (!defaultGroup) {
          defaultGroup = {
            category_id: null,
            category_name: "",
            questions: [],
            key: "uncategorized"
          };
          groups.push(defaultGroup);
        }

        defaultGroup.questions.push(item);
      }
    });

    return groups;
  }, [questions]);

  const shouldShowRatings =
    stepType != 2 &&
    isRatingEnabledQuestions == 1;

  const shouldHideInput = (q) => {
    const answerAllowedOnQuestion =
      selectedTimeframe?.is_answer_allow_on_question == null
        ? true
        : Number(selectedTimeframe.is_answer_allow_on_question) === 1;

    const shouldHideByCycleSetting =
      selectedTimeframe?.is_answer_allow_on_question != null &&
      !answerAllowedOnQuestion;

    const questionId =
      q?.assessment_question_id || q?.manager_assessment_question_id || q?.id;

    if (!accountVisibilityConfig) {
      return shouldHideByCycleSetting;
    }

    const { specialAccountId, allowedQuestionId } = accountVisibilityConfig;

    // If cycle-level answers are disabled, hide answer inputs globally
    if (!answerAllowedOnQuestion) {
      return true;
    }

    // If not special account → show inputs normally
    if (accountId !== specialAccountId) {
      return shouldHideByCycleSetting;
    }

    // Normalize to array (handles string OR array config safely)
    const allowedIds = Array.isArray(allowedQuestionId)
      ? allowedQuestionId
      : [allowedQuestionId].filter(id => id !== undefined);

    // Hide input if question is NOT in allowed list
    const shouldHideBySpecialAccount =
      allowedIds.length > 0 &&
      !allowedIds.includes(questionId);

    return shouldHideByCycleSetting || shouldHideBySpecialAccount;
  };

  /* 3️Render Input By Question Type          */

  const renderInput = (q, selectedValue, selectedAnswer) => {
    const type = q.question_type || q.answer_type;
    const options = Object.values(q.value || {});

    switch (type) {
      case "Radio":
        return options.map(option => (
          <Radio
            key={option}
            label={option}
            checked={selectedValue === option}
            onChange={() => onChange(q, option)}
            className="question-option-box"
          />
        ));

      case "ShortAnswer":
        return (
          <input
            className="bp3-input question-input-box"
            type="text"
            value={selectedValue}
            onChange={e => onChange(q, e.target.value)}
          />
        );

      case "TextBox":
        return (
          <TextArea
            rows={6}
            className="question-input-box"
            value={selectedValue}
            onChange={e => onChange(q, e.target.value)}
          />
        );

      case "DropDown":
        return (
          <HTMLSelect
            options={[
              { label: "Please Select", value: "" },
              ...options.map(o => ({ label: o, value: o }))
            ]}
            value={selectedValue}
            onChange={e => onChange(q, e.target.value)}
          />
        );

      case "Checkbox":
        return options.map((option, i) => {
          const key = `option${i + 1}`;
          // Handle both object and empty string cases
          const isChecked =
            typeof selectedValue === "object" &&
            selectedValue !== null &&
            selectedValue[key] === option;

          return (
            <Checkbox
              key={`checkbox-${i}-${option}`}
              label={option}
              checked={isChecked}
              onChange={e =>
                onCheckboxChange(
                  q,
                  { key, value: option },
                  e.target.checked
                )
              }
            />
          );
        });


      case "Files": {
        const basenameFromUrl = (url) => {
          try {
            if (!url || typeof url !== "string") return "";
            const withoutQuery = url.split("?")[0];
            const parts = withoutQuery.split("/");
            return parts[parts.length - 1] || "";
          } catch (_) {
            return "";
          }
        };

        const rawName =
          selectedAnswer?.assessment_file?.name || selectedAnswer?.assessment_value;

        const fileUrl = selectedAnswer?.assessment_file ? null : selectedAnswer?.file_url;

        // If backend stored URL in assessment_value, show only filename.
        const fileName =
          typeof rawName === "string" && rawName.startsWith("http")
            ? (basenameFromUrl(rawName) || basenameFromUrl(fileUrl))
            : (rawName || basenameFromUrl(fileUrl));

        const inputId = `file-${q.id}`;

        return (
          <div>
            {!fileName && (
              <>
                <label
                  htmlFor={inputId}
                  style={{
                    cursor: "pointer",
                    color: "#106ba3",
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  + Add file
                </label>

                <input
                  id={inputId}
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    onFileUpload(q, file);
                  }}
                />
              </>
            )}

            {fileName && (
              <div
                style={{
                  marginTop: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#f5f8fa",
                  border: "1px solid #d8e1e8",
                  borderRadius: 6,
                  padding: "6px 10px",
                  fontSize: 13,
                  gap: 8
                }}
              >
                {fileUrl ? (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#106ba3", textDecoration: "none" }}
                  >
                    📄 {fileName}
                  </a>
                ) : (
                  <span>📄 {fileName}</span>
                )}

                <span
                  style={{
                    cursor: "pointer",
                    color: "#d14343",
                    fontWeight: "bold",
                    fontSize: 14
                  }}
                  onClick={() => onFileUpload(q, null)}
                >
                  ✕
                </span>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  /* ------------------------------------------ */
  /* 4️⃣ Render Question Card                   */
  /* ------------------------------------------ */
  const renderQuestionCard = (q, index, total) => {
    const selectedAnswer = answerMap[q.id];
    const selectedValue = selectedAnswer?.assessment_value || "";
    const selectedRating = assessmentRatings[q.id] || "";

    const questionId = q?.assessment_question_id || q?.manager_assessment_question_id || q?.id;

    return (
      <Flexbox key={questionId || `q-fallback-${index}`} flexDirection="column">
        <Flexbox className="assessment-question-card">
          <Flexbox
            justifyContent="space-between"
            style={{ width: "100%" }}
            gap="20px"
          >
            <Flexbox
              flexDirection="column"
              className="question-container"
            >
              <p className="assessment-question-text">
                {q.question}
              </p>

              {!shouldHideInput(q.id) && renderInput(q, selectedValue, selectedAnswer)}
            </Flexbox>

            {shouldShowRatings && (
              <Flexbox className="rating-container">
                <div>
                  <FormGroup style={{ marginTop: "5px" }}>
                    <HTMLSelect
                      options={[
                        { label: "Select Rating", value: "" },
                        { label: "NA", value: -1 },
                        ...(
                          useRatingLabels && Array.isArray(ratingLabels) && (ratingLabels.length > 0 || ((selectedTimeframe.start_rating_value || 0) === 1 && (selectedTimeframe.end_rating_value || 0) === 3))
                            ? (ratingLabels.length > 0 ? ratingLabels : fallbackRatingLabels).map(rl => ({ label: rl.label, value: String(rl.value) }))
                            : Array.from(
                              {
                                length:
                                  (selectedTimeframe.end_rating_value || 0) -
                                  (selectedTimeframe.start_rating_value || 0) +
                                  1
                              },
                              (_, i) => {
                                const val = (selectedTimeframe.start_rating_value || 0) + i;
                                return { label: val.toString(), value: String(val) };
                              }
                            )
                        )
                      ]}
                      value={selectedRating}
                      onChange={e =>
                        onRatingChange(
                          q.id,
                          e.target.value,
                          q.weightage
                        )
                      }
                    />
                  </FormGroup>

                  {weightage && (
                    <p className="question-weightage">
                      Weightage: <strong>{q.weightage || "-"}</strong>
                    </p>
                  )}
                </div>
              </Flexbox>
            )}
          </Flexbox>
        </Flexbox>
      </Flexbox>
    );
  };

  /* ------------------------------------------ */
  /* 5️⃣ Final Render                           */
  /* ------------------------------------------ */
  return (
    <>
      {normalizedGroups.map((group, groupIndex) => {
        const isUncategorized = !group.category_id || group.category_id === "uncategorized" || group.category_id === "null";
        const displayName = isUncategorized ? "Uncategorized" : group.category_name;

        return (
          <React.Fragment key={group.key}>
            {/* Render category title ALWAYS */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "15px",
                  color: "#1870D5",
                  margin: "20px 10px 10px"
                }}
              >
                {displayName}
              </div>
              {isRatingEnabledQuestions == 1 && (
                <div>
                  <p className="answer-weightage"
                    style={{
                      fontSize: "20px",
                      margin: "20px 10px 10px",
                      fontWeight: "600",
                      color: "#1f4fd9",
                    }}>
                    <strong>
                      {(() => {
                        const rating =
                          categoryWiseRatings?.[group.category_id]?.avg;

                        if (rating === undefined || rating === null) return "";

                        return Number(rating).toFixed(2);
                      })()}
                    </strong>
                  </p>
                </div>
              )}
            </div>

            {/* Always render questions (even uncategorized) */}
            {group.questions.map((q, index) =>
              renderQuestionCard(
                q,
                index,
                group.questions.length
              )
            )}

            {/* ✅ COMMENT SECTION */}
            {(() => {
              const visibleRemarks = (group.remark_questions || []).filter(
                remark => remark?.question?.trim()
              );

              if (visibleRemarks.length === 0) {
                return null;
              }

              return (
                <div style={{ marginTop: 12, marginLeft: 10 }}>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      color: "#5c7080"
                    }}
                  >
                    Comment
                  </div>

                  {visibleRemarks.map((remark) => {
                    const remarkValue =
                      remarkAnswers?.[remark.id] || "";

                    return (
                      <CategoryRemarkInput
                        question={remark.question}
                        multiline={true}
                        value={remarkValue}
                        onChange={(val) =>
                          updateRemarkAnswer(remark.id, val)
                        }
                        width="60%"
                      />
                    );
                  })}
                </div>
              );
            })()}

            {groupIndex !== normalizedGroups.length - 1 && (
              <Divider
                style={{
                  marginTop: "20px",
                  marginBottom: "20px"
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default AssessmentQuestionList;